// frontend/src/features/aw/AWDashboard.jsx
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { getAccounts, getStats, getAssessors, deleteAccount, scrapeAccounts, scrapeAll, getSupportedCads, markPending, checkPending, getSchedule, saveSchedule, getScrapeProgress, getClients, updateAccount } from "./api";
import AWAOAModal from "./AWAOAModal";

const ST = {
  ba:      { dot: "#2dd468", text: "#50d87a", bg: "rgba(45,212,104,.08)",  border: "rgba(45,212,104,.22)",  label: "Coded" },
  other:   { dot: "#e8453a", text: "#e8736a", bg: "rgba(232,69,58,.08)",   border: "rgba(232,69,58,.22)",   label: "Other" },
  pending: { dot: "#e8a020", text: "#e8b040", bg: "rgba(232,160,32,.08)",  border: "rgba(232,160,32,.22)",  label: "Pending", pulse: true },
  none:    { dot: "#d4a020", text: "#e0c050", bg: "rgba(212,160,32,.08)",  border: "rgba(212,160,32,.22)",  label: "None" },
  unknown: { dot: "#6b6577", text: "#968ea0", bg: "rgba(107,101,119,.08)", border: "rgba(107,101,119,.22)", label: "Unknown" },
  error:   { dot: "#e88040", text: "#e89868", bg: "rgba(232,128,64,.08)",  border: "rgba(232,128,64,.22)",  label: "Error" },
};

export default function AWDashboard({ onImport, refreshKey }) {
  const [data, setData] = useState({ accounts: [], total: 0, page: 1, page_size: 50 });
  const [stats, setStats] = useState(null);
  const [assessors, setAssessors] = useState([]);
  const [filters, setFilters] = useState({ q: "", assessor: "", status: "", year: "" });
  const [sort, setSort] = useState("");  // e.g. "client", "-client", "cad", "-cad"
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [supportedCads, setSupportedCads] = useState([]);
  const [aoaCads, setAoaCads] = useState([]);
  const [aoaTarget, setAoaTarget] = useState(null);
  const [manualTarget, setManualTarget] = useState(null); // account object for manual update modal
  const [rowState, setRowState] = useState({});
  const [bulkScraping, setBulkScraping] = useState(false);
  const [scrapeProgress, setScrapeProgress] = useState(null);
  const [toast, setToast] = useState(null);
  const [confirmScrapeOpen, setConfirmScrapeOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [schedule, setScheduleState] = useState(null);
  const [clientMap, setClientMap] = useState({}); // {companyName: {client, contacts}}

  const showToast = (msg, type = "info") => {
    setToast({ msg, type, id: Date.now() });
    setTimeout(() => setToast(null), 5000);
  };

  const load = useCallback(async (p = page) => {
    setLoading(true);
    try {
      const params = { page: p, page_size: 50 };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = k === "year" ? parseInt(v) : v; });
      if (sort) params.sort = sort;
      setData(await getAccounts(params));
    } catch { /* noop */ }
    setLoading(false);
  }, [page, filters, sort]);

  const refreshStats = () => getStats().then(setStats).catch(() => {});
  useEffect(() => { load(1); setPage(1); }, [filters, sort]); // eslint-disable-line
  useEffect(() => { load(page); }, [page]); // eslint-disable-line
  // Reload when returning from other tabs (refreshKey changes)
  useEffect(() => { if (refreshKey) { load(page); refreshStats(); } }, [refreshKey]); // eslint-disable-line
  useEffect(() => {
    refreshStats();
    getAssessors().then(setAssessors).catch(() => {});
    getSupportedCads().then(d => {
      setSupportedCads(d.supported || []);
      setAoaCads(d.aoa_details || []);
    }).catch(() => {});
    getSchedule().then(setScheduleState).catch(() => {});
    // Build a lookup map: company name → client with contacts
    getClients().then(cls => {
      const map = {};
      for (const c of cls) {
        const hasContacts = c.contacts && c.contacts.length > 0;
        if (c.name) map[c.name.toLowerCase()] = { client: c, hasContacts };
        if (c.top_level_company) map[c.top_level_company.toLowerCase()] = { client: c, hasContacts };
      }
      setClientMap(map);
    }).catch(() => {});
  }, []);

  // ── Scrape one ──
  const handleScrapeOne = async (id) => {
    setRowState(s => ({ ...s, [id]: "scraping" }));
    try {
      const r = await scrapeAccounts({ account_ids: [id] });
      const res = r.results?.[0];
      setRowState(s => ({ ...s, [id]: "done" }));
      if (res) {
        setData(prev => ({ ...prev, accounts: prev.accounts.map(a => a.id !== id ? a : {
          ...a, agent_name: res.agent_name ?? a.agent_name, agent_status: res.agent_status ?? a.agent_status,
          agent_last_checked: new Date().toISOString(), agent_scrape_error: res.error ?? null,
        })}));
      }
      refreshStats();
      showToast(res?.error ? `Error: ${res.error}` : `${res?.agent_name || "No agent"} (${res?.agent_status})`,
        res?.error ? "error" : res?.agent_status === "ba" ? "success" : "warn");
      setTimeout(() => setRowState(s => { const n = { ...s }; delete n[id]; return n; }), 3000);
    } catch (e) {
      setRowState(s => ({ ...s, [id]: "error" }));
      showToast("Scrape failed: " + (e.response?.data?.detail || e.message), "error");
      setTimeout(() => setRowState(s => { const n = { ...s }; delete n[id]; return n; }), 5000);
    }
  };

  const handleScrapeAll = () => setConfirmScrapeOpen(true);
  const activeFilters = useMemo(() => {
    const f = {};
    if (filters.assessor) f.assessor = filters.assessor;
    if (filters.status) f.status = filters.status;
    if (filters.year) f.year = parseInt(filters.year);
    if (filters.q) f.q = filters.q;
    return Object.keys(f).length ? f : null;
  }, [filters]);

  const handleScrapeConfirmed = async (opts = {}) => {
    setConfirmScrapeOpen(false);
    try {
      const payload = { ...(activeFilters || {}) };
      if (opts.excludeHarris) payload.exclude_harris = true;
      await scrapeAll(payload);
      setBulkScraping(true);
      showToast(activeFilters ? "Filtered scrape started" : "Full scrape started", "info");
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      if (e.response?.status === 409) {
        setBulkScraping(true);
        showToast("Scrape already in progress", "info");
      } else {
        showToast("Scrape failed: " + detail, "error");
      }
    }
  };

  // Poll for scrape progress when bulk scraping
  useEffect(() => {
    if (!bulkScraping) { setScrapeProgress(null); return; }
    let cancelled = false;
    const poll = async () => {
      while (!cancelled) {
        try {
          const p = await getScrapeProgress();
          if (cancelled) break;
          setScrapeProgress(p);
          if (!p.running) {
            setBulkScraping(false);
            setScrapeProgress(null);
            load(page); refreshStats();
            showToast(`Scrape complete: ${p.success} success, ${p.errors} errors`, p.errors > 0 ? "warn" : "success");
            break;
          }
          // Refresh table periodically to show updated rows
          load(page);
        } catch { /* noop */ }
        await new Promise(r => setTimeout(r, 3000)); // poll every 3s
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [bulkScraping]); // eslint-disable-line

  // Passive poll: check every 15s if a scheduled scrape started while user is on the page
  useEffect(() => {
    if (bulkScraping) return; // active polling already handles this
    const id = setInterval(() => {
      getScrapeProgress().then(p => {
        if (p.running && !bulkScraping) {
          setBulkScraping(true);
          showToast("Scheduled scrape in progress", "info");
        }
      }).catch(() => {});
    }, 15000);
    // Also check immediately on mount
    getScrapeProgress().then(p => {
      if (p.running) { setBulkScraping(true); }
    }).catch(() => {});
    return () => clearInterval(id);
  }, [bulkScraping]); // eslint-disable-line

  const handleCheckPending = async () => {
    setBulkScraping(true);
    showToast("Re-checking stale pending accounts...", "info");
    try {
      const r = await checkPending(10);
      load(page); refreshStats();
      showToast(`Checked: ${r.success} updated, ${r.errors} errors`, r.errors > 0 ? "warn" : "success");
    } catch (e) { showToast("Check pending failed", "error"); }
    setBulkScraping(false);
  };

  const handleMarkPending = async (id) => {
    try {
      const updated = await markPending(id);
      setData(prev => ({ ...prev, accounts: prev.accounts.map(a => a.id !== id ? a : {
        ...a, agent_status: "pending", pending_since: new Date().toISOString(),
      })}));
      refreshStats();
      showToast("Marked as pending", "success");
    } catch { showToast("Failed to mark pending", "error"); }
  };

  const handleManualSave = async (id, updates) => {
    try {
      const updated = await updateAccount(id, updates);
      setData(prev => ({ ...prev, accounts: prev.accounts.map(a => a.id !== id ? a : {
        ...a, ...updates, agent_last_checked: new Date().toISOString(),
      })}));
      refreshStats();
      showToast("Updated", "success");
    } catch { showToast("Update failed", "error"); }
    setManualTarget(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this account?")) return;
    await deleteAccount(id);
    load(page); refreshStats();
  };

  const handleScheduleSave = async (cfg) => {
    try {
      const saved = await saveSchedule(cfg);
      setScheduleState(saved);
      showToast("Schedule saved", "success");
    } catch { showToast("Failed to save schedule", "error"); }
    setScheduleOpen(false);
  };

  const setF = (k, v) => setFilters(f => ({ ...f, [k]: v }));
  const toggleSort = (key) => setSort(s => s === key ? `-${key}` : s === `-${key}` ? "" : key);
  const totalPages = Math.ceil(data.total / data.page_size) || 1;
  const anyScraping = bulkScraping || Object.values(rowState).includes("scraping");


  return (
    <div>
      <style>{DASH_CSS}</style>

      {/* Toast */}
      {toast && (
        <div className={`aw-toast aw-toast-${toast.type}`} key={toast.id}>
          <div className="aw-toast-bar" />
          <span>{toast.msg}</span>
          <button className="aw-toast-x" onClick={() => setToast(null)}>&times;</button>
        </div>
      )}

      {/* ── Hero Stats ── */}
      {stats && (
        <div className="aw-stats">
          <StatCard n={stats.total} label="Total Accounts" color="#94a3b8" icon="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" delay={0} />
          <StatCard n={stats.supported_count} label="Supported" color="#3b82f6" icon="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2z" delay={1} />
          <StatCard n={stats.ba_count} label="Coded BA" color="#22c55e" glow icon="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" delay={2} />
          <StatCard n={stats.flagged_count} label="Needs Attention" color="#ef4444" glow icon="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" delay={3} />
          <StatCard n={stats.pending_count} label="Pending AOA" color="#f59e0b" pulse icon="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" delay={4} />
        </div>
      )}

      {/* ── Action Bar ── */}
      <div className="aw-actions">
        <button className={`aw-btn-primary ${anyScraping ? "aw-btn-active" : ""}`}
          onClick={handleScrapeAll} disabled={anyScraping}>
          {bulkScraping ? <><Spinner /> {scrapeProgress?.running ? `${scrapeProgress.processed}/${scrapeProgress.total}` : "Starting..."}</> : <>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
            Scrape All
          </>}
        </button>

        {stats?.pending_count > 0 && (
          <button className="aw-btn aw-btn-amber" onClick={handleCheckPending} disabled={anyScraping}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
            Check Pending ({stats.pending_count})
          </button>
        )}

        <button className="aw-btn aw-btn-blue" onClick={() => setScheduleOpen(true)}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
          Schedule
          {schedule?.enabled && <span className="aw-sched-dot" />}
        </button>

        <button className="aw-btn" onClick={onImport}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 4v16m8-8H4"/></svg>
          Import
        </button>

        <div style={{ flex: 1 }} />
        <CadSupportIndicator supported={supportedCads} aoa={aoaCads} />
      </div>

      {/* ── Scrape Progress Banner ── */}
      {scrapeProgress?.running && <ScrapeProgressBanner progress={scrapeProgress} />}

      {/* ── Filters ── */}
      <div className="aw-filters">
        <div className="aw-search-wrap">
          <svg className="aw-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input className="aw-search" placeholder="Search accounts..." value={filters.q}
            onChange={e => setF("q", e.target.value)} />
        </div>
        <div className="aw-pills">
          {Object.entries(ST).map(([k, v]) => (
            <button key={k} className={`aw-pill ${filters.status === k ? "aw-pill-on" : ""}`}
              style={{ "--pc": v.dot, "--pb": v.bg, "--pbo": v.border }}
              onClick={() => setF("status", filters.status === k ? "" : k)}>
              <span className="aw-pill-dot" /> {v.label}
            </button>
          ))}
        </div>
        <select className="aw-sel" value={filters.assessor} onChange={e => setF("assessor", e.target.value)}>
          <option value="">All CADs</option>
          {assessors.map(a => <option key={a.key} value={a.key}>{a.key.toUpperCase()} ({a.count})</option>)}
        </select>
        <input className="aw-input aw-input-sm" placeholder="Year" value={filters.year}
          onChange={e => setF("year", e.target.value)} />
        {(filters.q || filters.assessor || filters.status || filters.year) && (
          <button className="aw-clear" onClick={() => setFilters({ q: "", assessor: "", status: "", year: "" })}>Clear</button>
        )}
      </div>

      {/* ── Table ── */}
      <div className="aw-table-wrap">
        <table className="aw-table">
          <thead>
            <tr>
              {[
                ["Account #", "account"], ["Client", "client"], ["CAD", "cad"],
                ["Address", "address"], ["Year", "year"], ["Status", "status"],
                ["Agent / Fiduciary", "agent"], ["Checked", "checked"], ["", null],
              ].map(([label, key]) => (
                <th key={label || "actions"} className={key ? "aw-th-sort" : ""} onClick={key ? () => toggleSort(key) : undefined}>
                  {label}
                  {key && sort.replace("-","") === key && (
                    <svg className="aw-sort-arrow" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                      style={{ transform: sort.startsWith("-") ? "rotate(180deg)" : "none" }}>
                      <polyline points="6 15 12 9 18 15"/>
                    </svg>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && !data.accounts.length ? (
              <tr><td colSpan={9} style={{ padding: 48, textAlign: "center", color: "var(--dim)" }}>Loading...</td></tr>
            ) : !data.accounts.length ? (
              <tr><td colSpan={9} style={{ padding: 48, textAlign: "center", color: "var(--dim)" }}>
                No accounts found. Import some to get started.
              </td></tr>
            ) : data.accounts.map(a => {
              const s = ST[a.agent_status] || ST.unknown;
              const sup = supportedCads.includes((a.assessor_key || "").toLowerCase());
              const rs = rowState[a.id];
              return (
                <tr key={a.id} className={`aw-row ${rs === "scraping" ? "aw-row-scraping" : ""} ${rs === "done" ? "aw-row-done" : ""} ${rs === "error" ? "aw-row-err" : ""}`}>
                  <td><span className="aw-mono">{a.parcel_acct_num}</span></td>
                  <td>{(() => {
                    const match = a.company_name && clientMap[(a.company_name || "").toLowerCase()];
                    if (!a.company_name) return <span className="aw-dim">—</span>;
                    if (match?.hasContacts) {
                      const cts = match.client.contacts;
                      const fixN = n => { if (!n) return ""; const m = n.trim().match(/^([^,]+),\s*(.+)$/); return m ? `${m[2]} ${m[1]}` : n; };
                      const tip = cts.map(c => `${fixN(c.full_name)}${c.title ? " (" + c.title + ")" : ""}${c.email ? " — " + c.email : ""}`).join("\n");
                      return <span className="aw-client-linked" title={tip}>{a.company_name}</span>;
                    }
                    return <span className="aw-client-unlinked" title="No contacts on file">{a.company_name}</span>;
                  })()}</td>
                  <td><span className={`aw-cad-tag ${sup ? "aw-cad-sup" : ""}`}>{(a.assessor_key || a.assessor || "").toUpperCase()}</span></td>
                  <td>{a.site_address || <span className="aw-dim">—</span>}</td>
                  <td>{a.tax_year}</td>
                  <td>
                    <button className="aw-status aw-status-btn" style={{ "--sc": s.dot, "--sb": s.bg, "--sbo": s.border }}
                      onClick={() => setManualTarget(a)} title="Click to manually update status">
                      <span className={`aw-dot ${s.pulse ? "aw-dot-pulse" : ""}`} />
                      <span style={{ color: s.text }}>{s.label}</span>
                      {a.agent_status === "pending" && a.pending_since && (
                        <span className="aw-dim" style={{ fontSize: 9, marginLeft: 4 }}>
                          (sent {new Date(a.pending_since).toLocaleDateString("en-US", { month: "numeric", day: "numeric" })})
                        </span>
                      )}
                    </button>
                  </td>
                  <td style={{ color: a.agent_name ? s.text : "var(--dim)" }}>
                    {a.agent_name || "—"}
                    {a.notes && <span className="aw-note-dot" title={a.notes}>&#9998;</span>}
                    {a.agent_scrape_error && (a.agent_status === "error" || a.agent_status === "unknown") && (
                      <span className="aw-err-tip" title={a.agent_scrape_error}> !</span>
                    )}
                  </td>
                  <td className="aw-checked-cell">{a.agent_last_checked ? (() => {
                    const ts = a.agent_last_checked.endsWith("Z") ? a.agent_last_checked : a.agent_last_checked + "Z";
                    const mins = Math.floor((Date.now() - new Date(ts).getTime()) / 60000);
                    const short = mins < 1 ? "now" : mins < 60 ? `${mins}m` : mins < 1440 ? `${Math.floor(mins/60)}h` : `${Math.floor(mins/1440)}d`;
                    return <span className="aw-dim" title={new Date(ts).toLocaleString()}>{short}</span>;
                  })() : <span className="aw-dim">—</span>}</td>
                  <td className="aw-row-acts">
                    <button className="aw-act aw-act-orange" onClick={() => setAoaTarget(a.id)} title="Generate AOA">AOA</button>
                    {sup ? (
                      rs === "scraping" ? (
                        <span className="aw-act-spin"><Spinner size={11}/> Checking</span>
                      ) : (
                        <button className="aw-act aw-act-blue" onClick={() => handleScrapeOne(a.id)} disabled={anyScraping}>Check</button>
                      )
                    ) : (
                      <button className="aw-act" style={{ color: "var(--muted)" }} onClick={() => setManualTarget(a)}
                        title="Manually update agent status">Manual</button>
                    )}
                    {(a.agent_status === "other" || a.agent_status === "none") && (
                      <button className="aw-act aw-act-amber" onClick={() => handleMarkPending(a.id)} title="Mark as Pending (AOA sent)">Pending</button>
                    )}
                    <button className="aw-act aw-act-red aw-act-hover" onClick={() => handleDelete(a.id)}>Del</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="aw-pager">
          <button className="aw-pg-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</button>
          {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
            const p = totalPages <= 7 ? i + 1 : page <= 4 ? i + 1 : page >= totalPages - 3 ? totalPages - 6 + i : page - 3 + i;
            return (
              <button key={p} className={`aw-pg-num ${p === page ? "aw-pg-on" : ""}`} onClick={() => setPage(p)}>{p}</button>
            );
          })}
          <button className="aw-pg-btn" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
          <span className="aw-dim" style={{ fontSize: 11, marginLeft: 8 }}>{data.total} total</span>
        </div>
      )}

      {/* Modals */}
      {confirmScrapeOpen && <ConfirmScrapeModal
        total={data.supported_count || 0}
        totalAll={data.total || 0}
        harrisCount={data.harris_count || 0}
        smithCount={data.smith_count || 0}
        activeFilters={activeFilters}
        onConfirm={handleScrapeConfirmed}
        onClose={() => setConfirmScrapeOpen(false)}
      />}

      {aoaTarget && <AWAOAModal accountIds={[aoaTarget]} accounts={data.accounts.filter(a => a.id === aoaTarget)}
        clientMap={clientMap}
        onClose={() => { setAoaTarget(null); load(page); refreshStats(); }} />}

      {scheduleOpen && <ScheduleModal schedule={schedule} onSave={handleScheduleSave} onClose={() => setScheduleOpen(false)} />}

      {manualTarget && <ManualUpdateModal account={manualTarget}
        onSave={(updates) => handleManualSave(manualTarget.id, updates)}
        onClose={() => setManualTarget(null)} />}
    </div>
  );
}

// ── Sub-components ──

function StatCard({ n, label, color, glow, pulse, icon, delay, isText }) {
  return (
    <div className="aw-stat" style={{ "--c": color, animationDelay: `${delay * 50}ms` }}>
      <div className="aw-stat-top">
        <svg className="aw-stat-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d={icon}/></svg>
        <div className="aw-stat-l">{label}</div>
      </div>
      <div className={`aw-stat-n ${pulse ? "aw-stat-pulse" : ""}`} style={{ color }}>
        {n}
      </div>
    </div>
  );
}

function CadSupportIndicator({ supported, aoa }) {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const aoaSet = new Set(aoa);
  const withAoa = supported.filter(c => aoaSet.has(c));
  const checkOnly = supported.filter(c => !aoaSet.has(c));

  return (
    <div className="aw-cad-ind" ref={ref}>
      <button className="aw-cad-ind-btn" onClick={() => setOpen(o => !o)}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
        <span>{supported.length} CADs</span>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ opacity: 0.5, transform: open ? "rotate(180deg)" : "none", transition: "transform .2s var(--ease)" }}><polyline points="6 9 12 15 18 9"/></svg>
      </button>

      {open && (
        <div className="aw-cad-pop">
          {withAoa.length > 0 && (
            <div className="aw-cad-pop-section">
              <div className="aw-cad-pop-label">Agent Check + AOA</div>
              <div className="aw-cad-pop-list">
                {withAoa.map(c => (
                  <span key={c} className="aw-cad-pop-item aw-cad-pop-aoa">{c.toUpperCase()}</span>
                ))}
              </div>
            </div>
          )}
          {checkOnly.length > 0 && (
            <div className="aw-cad-pop-section">
              <div className="aw-cad-pop-label">Agent Check Only</div>
              <div className="aw-cad-pop-list">
                {checkOnly.map(c => (
                  <span key={c} className="aw-cad-pop-item">{c.toUpperCase()}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Spinner({ size = 13 }) {
  return <span style={{ display: "inline-block", width: size, height: size, border: "2px solid var(--border)", borderTopColor: "var(--accent)", borderRadius: "50%", animation: "aw-spin .6s linear infinite", flexShrink: 0 }} />;
}

function ScheduleModal({ schedule, onSave, onClose }) {
  const [cfg, setCfg] = useState({
    enabled: schedule?.enabled ?? true,
    hour: schedule?.hour ?? 2,
    minute: schedule?.minute ?? 0,
    frequency: schedule?.frequency ?? "once",
    timezone: schedule?.timezone ?? "America/Chicago",
  });
  const set = (k, v) => setCfg(c => ({ ...c, [k]: v }));
  const fmtTime = `${String(cfg.hour % 12 || 12)}:${String(cfg.minute).padStart(2, "0")} ${cfg.hour >= 12 ? "PM" : "AM"}`;
  const tzLabel = cfg.timezone.replace("America/", "").replace("_", " ");

  return (
    <div className="aw-modal-bg" onClick={onClose}>
      <div className="aw-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
        <div className="aw-modal-hdr">
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Scrape Schedule</h3>
          <button className="aw-modal-x" onClick={onClose}>&times;</button>
        </div>
        <div className="aw-modal-body">
          {/* Time pickers */}
          <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
            <div style={{ flex: 1 }}>
              <label className="aw-label">Hour</label>
              <select className="aw-sel" style={{ width: "100%" }} value={cfg.hour} onChange={e => set("hour", parseInt(e.target.value))}>
                {Array.from({ length: 24 }, (_, i) => <option key={i} value={i}>{i === 0 ? "12 AM" : i < 12 ? `${i} AM` : i === 12 ? "12 PM" : `${i - 12} PM`}</option>)}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label className="aw-label">Minute</label>
              <select className="aw-sel" style={{ width: "100%" }} value={cfg.minute} onChange={e => set("minute", parseInt(e.target.value))}>
                {Array.from({ length: 60 }, (_, m) => <option key={m} value={m}>{String(m).padStart(2, "0")}</option>)}
              </select>
            </div>
          </div>

          {/* Timezone */}
          <div style={{ marginBottom: 20 }}>
            <label className="aw-label">Timezone</label>
            <select className="aw-sel" style={{ width: "100%" }} value={cfg.timezone} onChange={e => set("timezone", e.target.value)}>
              {["America/Chicago", "America/New_York", "America/Denver", "America/Los_Angeles", "America/Phoenix", "UTC"].map(tz => (
                <option key={tz} value={tz}>{tz.replace("America/", "").replace("_", " ")} ({tz})</option>
              ))}
            </select>
          </div>

          {/* Repeat daily toggle */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, padding: "12px 14px", borderRadius: 10, background: "var(--surface)", border: "1px solid var(--border-l)" }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>Repeat daily</div>
              <div style={{ fontSize: 10, color: "var(--dim)", marginTop: 2 }}>
                {cfg.frequency === "daily" ? "Runs every day at this time" : "Runs once at the next occurrence, then stops"}
              </div>
            </div>
            <button className={`aw-toggle ${cfg.frequency === "daily" ? "aw-toggle-on" : ""}`}
              onClick={() => set("frequency", cfg.frequency === "daily" ? "once" : "daily")}>
              <span className="aw-toggle-dot" />
            </button>
          </div>

          {/* Preview */}
          <div style={{ padding: "10px 14px", borderRadius: 8, background: "rgba(74,142,245,.08)", border: "1px solid rgba(74,142,245,.15)", fontSize: 12.5, color: "#7db5f0" }}>
            {cfg.frequency === "daily"
              ? `Scrapes every day at ${fmtTime} (${tzLabel})`
              : `One-time scrape at the next ${fmtTime} (${tzLabel})`
            }
          </div>
        </div>
        <div className="aw-modal-foot">
          <button className="aw-btn" onClick={onClose}>Cancel</button>
          <button className="aw-btn" style={{ borderColor: "rgba(232,69,58,.25)", color: "#e8736a" }}
            onClick={() => onSave({ ...cfg, enabled: false })}>Disable</button>
          <button className="aw-btn-primary" onClick={() => onSave({ ...cfg, enabled: true })}>
            {cfg.frequency === "daily" ? "Save & Enable" : "Schedule"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ManualUpdateModal({ account, onSave, onClose }) {
  const [agentName, setAgentName] = useState(account.agent_name || "");
  const [status, setStatus] = useState(account.agent_status || "unknown");
  const [notes, setNotes] = useState(account.notes || "");

  const statusOptions = [
    { key: "ba", label: "Coded BA", color: "#2dd468" },
    { key: "other", label: "Other Agent", color: "#e8453a" },
    { key: "none", label: "No Agent", color: "#d4a020" },
    { key: "pending", label: "Pending AOA", color: "#e8a020" },
    { key: "unknown", label: "Unknown", color: "#6b6577" },
  ];

  const handleStatusChange = (key) => {
    setStatus(key);
    if (key === "ba") setAgentName("BA Property Tax");
  };

  return (
    <div className="aw-modal-bg" onClick={onClose}>
      <div className="aw-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
        <div className="aw-modal-hdr">
          <div>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Update Account</h3>
            <div style={{ fontSize: 11, color: "var(--dim)", marginTop: 2 }}>
              {account.parcel_acct_num} &middot; {(account.assessor_key || account.assessor || "").toUpperCase()}
            </div>
          </div>
          <button className="aw-modal-x" onClick={onClose}>&times;</button>
        </div>
        <div className="aw-modal-body">
          {/* Status quick-pick */}
          <label className="aw-label" style={{ marginBottom: 8 }}>Agent Status</label>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
            {statusOptions.map(opt => (
              <button key={opt.key} onClick={() => handleStatusChange(opt.key)}
                style={{
                  padding: "6px 12px", borderRadius: 8, fontSize: 11, fontWeight: 700,
                  fontFamily: "inherit", cursor: "pointer", transition: "all .15s",
                  border: `1px solid ${status === opt.key ? opt.color : "var(--border)"}`,
                  background: status === opt.key ? `${opt.color}18` : "transparent",
                  color: status === opt.key ? opt.color : "var(--dim)",
                }}>
                <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: opt.color, marginRight: 5 }} />
                {opt.label}
              </button>
            ))}
          </div>

          {/* Agent name */}
          <div style={{ marginBottom: 12 }}>
            <label className="aw-label">Agent / Fiduciary Name</label>
            <input className="aw-input" style={{ width: "100%" }} value={agentName}
              onChange={e => setAgentName(e.target.value)}
              placeholder="e.g. BA Property Tax, O'Connor & Associates" />
          </div>

          {/* Notes */}
          <div>
            <label className="aw-label">Notes</label>
            <textarea className="aw-input" style={{ width: "100%", minHeight: 70, resize: "vertical" }}
              value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="e.g. AOA mailed 3/14, called CAD office, waiting for confirmation..." />
          </div>
        </div>
        <div className="aw-modal-foot">
          <button className="aw-btn" onClick={onClose}>Cancel</button>
          <button className="aw-btn-primary" onClick={() => onSave({ agent_name: agentName || null, agent_status: status, notes })}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfirmScrapeModal({ total, totalAll, harrisCount, smithCount, activeFilters, onConfirm, onClose }) {
  const [excludeHarris, setExcludeHarris] = useState(false);
  const skipped = (totalAll || 0) - total;
  const effectiveHarris = excludeHarris ? 0 : (harrisCount || 0);
  const effectiveTotal = excludeHarris ? total - (harrisCount || 0) : total;
  const effectiveSmith = smithCount || 0;
  const otherCount = effectiveTotal - effectiveHarris - effectiveSmith;

  // Time estimate: Harris ~67s, Smith ~3s, everything else ~1.9s
  const estSeconds = Math.ceil(Math.max(0, otherCount) * 1.9 + effectiveSmith * 3 + effectiveHarris * 67);
  const estMinutes = Math.ceil(estSeconds / 60);
  const estText = estMinutes < 60
    ? `~${estMinutes} min`
    : `~${Math.floor(estMinutes / 60)}h ${estMinutes % 60}m`;

  const filterLabels = activeFilters ? Object.entries(activeFilters).map(([k, v]) => {
    if (k === "assessor") return v.toUpperCase();
    if (k === "status") return `status: ${v}`;
    if (k === "year") return `year: ${v}`;
    if (k === "q") return `"${v}"`;
    return `${k}: ${v}`;
  }) : [];

  return (
    <div className="aw-modal-bg" onClick={onClose}>
      <div className="aw-modal aw-confirm-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="aw-modal-hdr">
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
            {activeFilters ? "Start Filtered Agent Check" : "Start Agent Check"}
          </h3>
          <button className="aw-modal-x" onClick={onClose}>&times;</button>
        </div>
        <div className="aw-modal-body">
          {/* Active filters notice */}
          {activeFilters && (
            <div className="aw-confirm-card" style={{ marginBottom: 16, background: "rgba(232,160,32,.05)", borderColor: "rgba(232,160,32,.15)" }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#e8b040" strokeWidth="2" strokeLinecap="round"><path d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"/></svg>
              <div>
                <div style={{ fontWeight: 700, fontSize: 12, color: "#e8b040" }}>Filters active</div>
                <div style={{ fontSize: 11, color: "var(--dim)", marginTop: 2 }}>
                  Only checking accounts matching: {filterLabels.join(", ")}
                </div>
              </div>
            </div>
          )}

          {/* Account summary */}
          <div className="aw-confirm-grid">
            <div className="aw-confirm-stat">
              <div className="aw-confirm-stat-n">{effectiveTotal}</div>
              <div className="aw-confirm-stat-l">Accounts to check</div>
            </div>
            <div className="aw-confirm-stat">
              <div className="aw-confirm-stat-n" style={{ color: "#7db5f0" }}>{estText}</div>
              <div className="aw-confirm-stat-l">Estimated time</div>
            </div>
          </div>
          {skipped > 0 && (
            <div style={{ fontSize: 11, color: "var(--dim)", textAlign: "center", marginTop: -8, marginBottom: 12 }}>
              {skipped} account{skipped !== 1 ? "s" : ""} in unsupported CADs will be skipped
            </div>
          )}

          {/* Exclude Harris toggle */}
          {harrisCount > 0 && (
            <label className="aw-confirm-harris-toggle" onClick={e => e.stopPropagation()}>
              <input type="checkbox" checked={excludeHarris}
                onChange={e => setExcludeHarris(e.target.checked)} />
              <span>Exclude Harris County ({harrisCount} account{harrisCount !== 1 ? "s" : ""}, ~{Math.ceil(harrisCount * 67 / 60)} min)</span>
            </label>
          )}

          {/* Info cards */}
          <div className="aw-confirm-info">
            <div className="aw-confirm-card aw-confirm-card-green">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#50d87a" strokeWidth="2" strokeLinecap="round"><path d="M9 12l2 2 4-4"/><circle cx="12" cy="12" r="10"/></svg>
              <div>
                <div style={{ fontWeight: 700, fontSize: 12 }}>Safe to leave this page</div>
                <div style={{ fontSize: 11, color: "var(--dim)", marginTop: 2 }}>The scrape runs on the server. You can close the browser, navigate away, or shut down your computer. Results are saved as each account is processed.</div>
              </div>
            </div>
            <div className="aw-confirm-card">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7db5f0" strokeWidth="2" strokeLinecap="round"><path d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z"/></svg>
              <div>
                <div style={{ fontWeight: 700, fontSize: 12 }}>Live progress if you stay</div>
                <div style={{ fontSize: 11, color: "var(--dim)", marginTop: 2 }}>Keep this page open to see real-time progress, or come back later — the banner will pick up where it left off.</div>
              </div>
            </div>
          </div>
        </div>
        <div className="aw-modal-foot">
          <button className="aw-btn" onClick={onClose}>Cancel</button>
          <button className="aw-btn-primary aw-confirm-go" onClick={() => onConfirm({ excludeHarris })}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
            {activeFilters ? "Start Filtered Scrape" : "Start Scrape"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ScrapeProgressBanner({ progress }) {
  const pct = progress.total ? (progress.processed / progress.total) * 100 : 0;
  const pctText = Math.round(pct);

  // Elapsed time — ensure UTC parsing (append Z if missing)
  const startedAt = progress.started_at
    ? (progress.started_at.endsWith("Z") ? progress.started_at : progress.started_at + "Z")
    : null;
  const elapsed = startedAt ? Math.max(0, Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000)) : 0;
  const fmtTime = (s) => {
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ${s % 60}s`;
    return `${Math.floor(m / 60)}h ${m % 60}m`;
  };

  // ETA — use actual rate once we have data, otherwise show "calculating..."
  const rate = elapsed > 0 && progress.processed > 0 ? elapsed / progress.processed : 0;
  const remaining = rate > 0 ? Math.ceil((progress.total - progress.processed) * rate) : 0;
  const etaText = remaining > 0 ? fmtTime(remaining) : "calculating...";

  return (
    <div className="aw-progress-banner-v2">
      {/* Animated background shimmer */}
      <div className="aw-progress-shimmer" />

      <div className="aw-progress-top">
        <div className="aw-progress-left">
          <div className="aw-progress-pulse-dot" />
          <span className="aw-progress-title">Agent Check in Progress</span>
        </div>
        <div className="aw-progress-right">
          <span className="aw-progress-safe">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#50d87a" strokeWidth="2.5" strokeLinecap="round"><path d="M9 12l2 2 4-4"/><circle cx="12" cy="12" r="10"/></svg>
            Safe to leave
          </span>
        </div>
      </div>

      {/* Big progress bar */}
      <div className="aw-progress-bar-v2-wrap">
        <div className="aw-progress-bar-v2" style={{ width: `${pct}%` }}>
          <div className="aw-progress-bar-glow" />
        </div>
      </div>

      {/* Stats row */}
      <div className="aw-progress-details">
        <div className="aw-progress-detail-item">
          <span className="aw-progress-detail-n">{progress.processed}</span>
          <span className="aw-progress-detail-sep">/</span>
          <span className="aw-progress-detail-total">{progress.total}</span>
          <span className="aw-progress-detail-pct">({pctText}%)</span>
        </div>

        {progress.current_cad && (
          <div className="aw-progress-detail-item aw-progress-current">
            <span className="aw-progress-cad-badge">{progress.current_cad.toUpperCase()}</span>
            <span className="aw-mono" style={{ fontSize: 11 }}>{progress.current_account}</span>
          </div>
        )}

        <div className="aw-progress-detail-item" style={{ gap: 12 }}>
          <span style={{ color: "#50d87a" }}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M20 6L9 17l-5-5"/></svg>
            {" "}{progress.success}
          </span>
          {progress.errors > 0 && (
            <span style={{ color: "#e8736a" }}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M18 6L6 18M6 6l12 12"/></svg>
              {" "}{progress.errors}
            </span>
          )}
        </div>

        <div className="aw-progress-detail-item aw-progress-time">
          <span>{fmtTime(elapsed)} elapsed</span>
          <span className="aw-dim">&middot;</span>
          <span>{etaText} remaining</span>
        </div>
      </div>
    </div>
  );
}

const DASH_CSS = `
/* ── Stats Strip ── */
.aw-stats{display:flex;gap:10px;margin-bottom:20px;flex-wrap:wrap;}
@media(max-width:700px){.aw-stats{display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));}}

.aw-stat{
  flex:1;min-width:120px;
  padding:14px 16px;border-radius:10px;
  background:var(--surface);border:1px solid var(--border-l);
  animation:aw-in .4s var(--ease) both;
  transition:border-color .2s var(--ease);
}
.aw-stat:hover{border-color:color-mix(in srgb,var(--c) 40%,var(--border));}
.aw-stat-top{display:flex;align-items:center;gap:6px;margin-bottom:8px;}
.aw-stat-icon{opacity:.6;flex-shrink:0;}
.aw-stat-n{font-size:24px;font-weight:800;line-height:1;letter-spacing:-.3px;}
.aw-stat-pulse{animation:aw-pulse 2s ease-in-out infinite;}
.aw-stat-l{font-size:10.5px;font-weight:600;color:var(--dim);text-transform:uppercase;letter-spacing:.5px;}

/* ── Actions ── */
.aw-actions{display:flex;align-items:center;gap:8px;margin-bottom:14px;flex-wrap:wrap;}
.aw-btn-amber{border-color:rgba(232,160,32,.25);color:#e8b040;}
.aw-btn-amber:hover{background:rgba(232,160,32,.08);border-color:rgba(232,160,32,.4);}
.aw-btn-blue{border-color:rgba(74,142,245,.25);color:#7db5f0;position:relative;}
.aw-btn-blue:hover{background:rgba(74,142,245,.08);border-color:rgba(74,142,245,.4);}
.aw-btn-active{animation:aw-glow 1.5s ease-in-out infinite;}
.aw-sched-dot{width:6px;height:6px;border-radius:50%;background:var(--green);display:inline-block;margin-left:4px;}

/* ── CAD support indicator ── */
.aw-cad-ind{position:relative;}
.aw-cad-ind-btn{
  display:inline-flex;align-items:center;gap:5px;
  padding:6px 12px;border-radius:7px;border:1px solid rgba(45,212,104,.18);
  background:rgba(45,212,104,.04);color:#50d87a;
  font-size:12px;font-weight:700;font-family:inherit;cursor:pointer;
  transition:all .15s var(--ease);
}
.aw-cad-ind-btn:hover{background:rgba(45,212,104,.08);border-color:rgba(45,212,104,.3);}
.aw-cad-pop{
  position:absolute;top:calc(100% + 6px);right:0;z-index:30;
  min-width:240px;padding:12px;border-radius:10px;
  background:var(--surface2);border:1px solid var(--border);
  box-shadow:0 8px 30px rgba(12,10,14,.5);
  animation:aw-in .2s var(--ease) both;
}
.aw-cad-pop-section{margin-bottom:10px;}
.aw-cad-pop-section:last-child{margin-bottom:0;}
.aw-cad-pop-label{
  font-size:9.5px;font-weight:700;color:var(--dim);
  text-transform:uppercase;letter-spacing:.6px;margin-bottom:6px;
}
.aw-cad-pop-list{display:flex;flex-wrap:wrap;gap:4px;}
.aw-cad-pop-item{
  padding:3px 8px;border-radius:4px;font-size:10.5px;font-weight:700;letter-spacing:.4px;
  background:rgba(107,101,119,.08);color:var(--muted);border:1px solid var(--border-l);
}
.aw-cad-pop-aoa{
  background:rgba(45,212,104,.06);color:#50d87a;border-color:rgba(45,212,104,.18);
}

/* ── Filters ── */
.aw-filters{display:flex;align-items:center;gap:8px;margin-bottom:14px;flex-wrap:wrap;}
.aw-search-wrap{position:relative;flex:0 0 220px;}
.aw-search-icon{position:absolute;left:10px;top:50%;transform:translateY(-50%);color:var(--dim);pointer-events:none;}
.aw-search{
  width:100%;padding:8px 11px 8px 30px;border-radius:7px;
  border:1px solid var(--border);background:var(--surface);
  color:var(--text);font-size:13px;font-family:inherit;outline:none;
  transition:border-color .2s var(--ease);
}
.aw-search:focus{border-color:var(--accent);}
.aw-pills{display:flex;gap:4px;flex-wrap:wrap;}
.aw-pill{
  display:inline-flex;align-items:center;gap:4px;
  padding:5px 11px;border-radius:6px;border:1px solid var(--pbo);
  background:transparent;color:var(--muted);font-size:10.5px;font-weight:600;
  font-family:inherit;cursor:pointer;transition:all .15s var(--ease);
}
.aw-pill:hover{background:var(--pb);}
.aw-pill-on{background:var(--pb);color:var(--text);border-color:var(--pc);}
.aw-pill-dot{width:6px;height:6px;border-radius:50%;background:var(--pc);}
.aw-input-sm{width:70px;}
.aw-clear{background:none;border:none;color:var(--accent);font-size:11.5px;font-weight:600;cursor:pointer;font-family:inherit;}

/* ── Table ── */
.aw-table-wrap{
  border-radius:10px;border:1px solid var(--border-l);
  background:var(--surface);overflow:auto;
}
.aw-table{width:100%;border-collapse:collapse;font-size:13px;}
.aw-table thead th{
  text-align:left;padding:11px 12px;
  background:rgba(20,18,24,.5);color:var(--dim);
  font-size:10.5px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;
  white-space:nowrap;border-bottom:1px solid var(--border-l);
  user-select:none;
}
.aw-th-sort{cursor:pointer;transition:color .15s var(--ease);}
.aw-th-sort:hover{color:var(--muted);}
.aw-sort-arrow{margin-left:4px;vertical-align:middle;transition:transform .2s var(--ease);color:var(--accent);}
.aw-row{border-bottom:1px solid rgba(60,55,64,.25);transition:background .2s var(--ease);}
.aw-row:nth-child(even){background:rgba(30,28,35,.15);}
.aw-row:hover{background:rgba(139,35,50,.05);}
.aw-row td{padding:9px 12px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:200px;}
.aw-row-scraping{background:rgba(74,142,245,.05) !important;}
.aw-row-done{animation:aw-row-flash-green 1.5s ease;}
.aw-row-err{animation:aw-row-flash-red 1.5s ease;}
@keyframes aw-row-flash-green{0%{background:rgba(45,212,104,.12);}100%{background:transparent;}}
@keyframes aw-row-flash-red{0%{background:rgba(232,69,58,.12);}100%{background:transparent;}}
.aw-mono{font-size:12px;letter-spacing:.3px;color:var(--muted);font-variant-numeric:tabular-nums;}
.aw-dim{color:var(--dim);}
/* Client link indicators */
.aw-client-linked{
  color:#50d87a;font-weight:600;cursor:help;
  border-bottom:1px dotted rgba(45,212,104,.35);
}
.aw-client-unlinked{color:var(--dim);cursor:help;border-bottom:1px dotted var(--border);}

.aw-cad-tag{
  padding:2px 6px;border-radius:4px;font-size:10.5px;font-weight:700;letter-spacing:.4px;
  background:rgba(107,101,119,.12);color:var(--dim);
}
.aw-cad-sup{background:rgba(45,212,104,.08);color:#50d87a;border:1px solid rgba(45,212,104,.18);}
.aw-status{
  display:inline-flex;align-items:center;gap:5px;
  padding:4px 10px;border-radius:6px;font-size:10.5px;font-weight:700;
  background:var(--sb);border:1px solid var(--sbo);
}
.aw-dot{width:6px;height:6px;border-radius:50%;background:var(--sc);flex-shrink:0;}
.aw-dot-pulse{animation:aw-pulse 1.5s ease-in-out infinite;}
.aw-status-btn{cursor:pointer;font-family:inherit;transition:all .15s var(--ease);}
.aw-status-btn:hover{filter:brightness(1.15);}
.aw-note-dot{color:#7db5f0;font-size:12px;margin-left:5px;cursor:help;opacity:.7;}
.aw-note-dot:hover{opacity:1;}
.aw-err-tip{color:#e88040;font-weight:800;cursor:help;margin-left:4px;}

/* Row actions */
.aw-checked-cell{font-size:11px;white-space:nowrap;text-align:center;}
.aw-row-acts{white-space:nowrap;}
.aw-act-hover{opacity:0;transition:opacity .15s var(--ease);}
.aw-row:hover .aw-act-hover{opacity:1;}
.aw-act-spin{display:inline-flex;align-items:center;gap:4px;font-size:10.5px;color:#7db5f0;font-weight:600;margin-right:6px;}

/* ── Pagination ── */
.aw-pager{display:flex;align-items:center;justify-content:center;gap:4px;margin-top:16px;padding:8px;}
.aw-pg-btn{
  padding:7px 14px;border-radius:6px;border:1px solid var(--border);
  background:transparent;color:var(--muted);font-size:12px;font-weight:600;
  cursor:pointer;font-family:inherit;transition:all .15s var(--ease);
}
.aw-pg-btn:hover{border-color:var(--accent);color:var(--accent);}
.aw-pg-btn:disabled{opacity:.3;cursor:not-allowed;}
.aw-pg-num{
  width:32px;height:32px;border-radius:6px;border:none;
  background:transparent;color:var(--dim);font-size:12px;font-weight:600;
  cursor:pointer;font-family:inherit;display:flex;align-items:center;justify-content:center;transition:all .15s var(--ease);
}
.aw-pg-num:hover{color:var(--text);background:var(--surface2);}
.aw-pg-on{background:var(--accent);color:#f5e8ea;}

/* ── Toast ── */
.aw-toast{
  position:fixed;top:16px;right:16px;z-index:100;
  display:flex;align-items:center;gap:10px;
  padding:11px 16px 11px 6px;border-radius:8px;
  background:var(--surface2);border:1px solid var(--border);
  box-shadow:0 6px 24px rgba(12,10,14,.4);
  font-size:13px;font-weight:500;
  animation:aw-slidein .3s var(--ease);
}
.aw-toast-bar{width:3px;align-self:stretch;border-radius:3px;flex-shrink:0;}
.aw-toast-success .aw-toast-bar{background:var(--green);}
.aw-toast-error .aw-toast-bar{background:var(--red);}
.aw-toast-warn .aw-toast-bar{background:var(--amber);}
.aw-toast-info .aw-toast-bar{background:var(--blue);}
.aw-toast-x{background:none;border:none;color:var(--dim);font-size:18px;cursor:pointer;line-height:1;padding:0 0 0 8px;}

/* ── Toggle ── */
.aw-toggle{
  width:44px;height:24px;border-radius:12px;border:none;
  background:var(--border);cursor:pointer;position:relative;
  transition:background .2s var(--ease);padding:0;
}
.aw-toggle-on{background:var(--green);}
.aw-toggle-dot{
  position:absolute;top:3px;left:3px;width:18px;height:18px;
  border-radius:50%;background:#f0eaed;transition:transform .2s var(--ease);
}
.aw-toggle-on .aw-toggle-dot{transform:translateX(20px);}

/* ── Confirm Scrape Modal ── */
.aw-confirm-modal .aw-modal-body{padding:20px 24px;}
.aw-confirm-grid{
  display:grid;grid-template-columns:repeat(2,1fr);gap:12px;margin-bottom:20px;
}
.aw-confirm-stat{
  padding:14px;border-radius:10px;
  background:rgba(20,18,24,.45);border:1px solid var(--border-l);
  text-align:center;
}
.aw-confirm-stat-n{font-size:20px;font-weight:800;color:var(--text);line-height:1;margin-bottom:4px;}
.aw-confirm-stat-l{font-size:10.5px;font-weight:600;color:var(--dim);text-transform:uppercase;letter-spacing:.4px;}
.aw-confirm-info{display:flex;flex-direction:column;gap:10px;}
.aw-confirm-card{
  display:flex;gap:12px;align-items:flex-start;
  padding:14px;border-radius:8px;
  background:rgba(74,142,245,.05);border:1px solid rgba(74,142,245,.12);
}
.aw-confirm-card svg{flex-shrink:0;margin-top:1px;}
.aw-confirm-card-green{
  background:rgba(45,212,104,.05);border-color:rgba(45,212,104,.12);
}
.aw-confirm-go{gap:6px;}
.aw-confirm-harris-toggle{
  display:flex;align-items:center;gap:8px;
  padding:8px 12px;margin:-4px 0 12px;border-radius:6px;
  background:rgba(232,160,32,.06);border:1px solid rgba(232,160,32,.15);
  font-size:12px;color:var(--text);cursor:pointer;user-select:none;
}
.aw-confirm-harris-toggle input{accent-color:#e8a020;cursor:pointer;}
.aw-confirm-harris-toggle span{font-weight:500;}

/* ── Progress Banner V2 ── */
.aw-progress-banner-v2{
  position:relative;overflow:hidden;
  padding:16px 20px;border-radius:10px;margin-bottom:16px;
  background:rgba(139,35,50,.06);
  border:1px solid rgba(139,35,50,.25);
  animation:aw-in .4s var(--ease) both;
}
.aw-progress-shimmer{
  position:absolute;inset:0;
  background:linear-gradient(90deg, transparent 0%, rgba(139,35,50,.04) 45%, rgba(139,35,50,.06) 50%, rgba(139,35,50,.04) 55%, transparent 100%);
  background-size:200% 100%;
  animation:aw-shimmer 3s ease-in-out infinite;
  pointer-events:none;
}
@keyframes aw-shimmer{0%{background-position:200% 0;}100%{background-position:-200% 0;}}

.aw-progress-top{display:flex;align-items:center;justify-content:space-between;margin-bottom:12px;}
.aw-progress-left{display:flex;align-items:center;gap:10px;}
.aw-progress-pulse-dot{
  width:7px;height:7px;border-radius:50%;
  background:#cf4154;
  animation:aw-pulse 1.5s ease-in-out infinite;
}
.aw-progress-title{font-size:14px;font-weight:700;letter-spacing:-.1px;}
.aw-progress-right{display:flex;align-items:center;gap:8px;}
.aw-progress-safe{
  display:inline-flex;align-items:center;gap:5px;
  font-size:11.5px;font-weight:600;color:#50d87a;
  padding:3px 10px;border-radius:6px;
  background:rgba(45,212,104,.06);border:1px solid rgba(45,212,104,.12);
}

/* Progress bar */
.aw-progress-bar-v2-wrap{
  height:6px;border-radius:3px;
  background:rgba(60,55,64,.4);
  overflow:hidden;margin-bottom:14px;
}
.aw-progress-bar-v2{
  height:100%;border-radius:3px;
  background:var(--accent);
  transition:width .8s cubic-bezier(.4,0,.2,1);
  position:relative;
}
.aw-progress-bar-glow{
  position:absolute;right:0;top:0;bottom:0;width:30px;
  background:linear-gradient(90deg,transparent,rgba(240,234,237,.12));
  border-radius:0 3px 3px 0;
  animation:aw-glow-sweep 2s ease-in-out infinite;
}
@keyframes aw-glow-sweep{0%,100%{opacity:.3;}50%{opacity:1;}}

/* Stats details row */
.aw-progress-details{
  display:flex;align-items:center;gap:16px;flex-wrap:wrap;
  font-size:12.5px;font-weight:600;
}
.aw-progress-detail-item{display:flex;align-items:center;gap:4px;}
.aw-progress-detail-n{font-size:15px;font-weight:800;color:var(--text);}
.aw-progress-detail-sep{color:var(--dim);font-weight:400;}
.aw-progress-detail-total{color:var(--dim);font-size:13px;font-weight:600;}
.aw-progress-detail-pct{color:var(--muted);font-size:11.5px;font-weight:500;margin-left:2px;}
.aw-progress-current{
  padding:2px 8px;border-radius:5px;
  background:rgba(74,142,245,.06);border:1px solid rgba(74,142,245,.12);
  gap:6px;
}
.aw-progress-cad-badge{
  font-size:9.5px;font-weight:800;letter-spacing:.4px;
  padding:1px 6px;border-radius:3px;
  background:rgba(45,212,104,.1);color:#50d87a;
  border:1px solid rgba(45,212,104,.15);
}
.aw-progress-time{margin-left:auto;color:var(--muted);font-weight:500;gap:6px;font-size:11.5px;}

/* (aw-label defined in AWApp.jsx) */
`;
